import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
}

interface EditSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: TrainingSession | null;
  onSessionUpdated?: () => void;
}

const EditSessionDialog: React.FC<EditSessionDialogProps> = ({
  open,
  onOpenChange,
  session,
  onSessionUpdated
}) => {
  useUiLanguage();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (session) {
      setTitle(session.title);
      setDate(session.date);
      setStartTime(session.start_time);
      setEndTime(session.end_time);
      setMaxParticipants(session.max_participants?.toString() || '');
      setDescription(session.description || '');
    }
  }, [session]);

  const handleUpdateSession = async () => {
    if (!session) return;

    if (!title.trim() || !date || !startTime || !endTime) {
      toast({
        title: uiText("Validation Error"),
        description: uiText("Please fill in all required fields."),
        variant: "destructive"
      });
      return;
    }

    if (startTime >= endTime) {
      toast({
        title: uiText("Validation Error"),
        description: uiText("End time must be after start time."),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('training_sessions')
        .update({
          title: title.trim(),
          date,
          start_time: startTime,
          end_time: endTime,
          max_participants: maxParticipants ? parseInt(maxParticipants) : null,
          description: description.trim() || null
        })
        .eq('id', session.id);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Training session updated successfully!")
      });

      onOpenChange(false);
      if (onSessionUpdated) {
        onSessionUpdated();
      }
    } catch (error) {
      console.error('Error updating session:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update training session."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{uiText("Edit Training Session")}</DialogTitle>
          <DialogDescription>
            {uiText("Update the training session details.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">{uiText("Session Title *")}</Label>
            <Input
              id="title"
              placeholder={uiText("e.g., Basic Training, Kata Practice")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">{uiText("Date *")}</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="maxParticipants">{uiText("Max Participants")}</Label>
              <Input
                id="maxParticipants"
                type="number"
                placeholder="e.g., 15"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">{uiText("Start Time *")}</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endTime">{uiText("End Time *")}</Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">{uiText("Description (Optional)")}</Label>
            <Textarea
              id="description"
              placeholder={uiText("Additional details about the session...")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {uiText("Cancel")}</Button>
          <Button
            onClick={handleUpdateSession}
            disabled={isLoading}
            className="bg-red-900 hover:bg-red-800"
          >
            {isLoading ? uiText("Updating...") : uiText("Update Session")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSessionDialog;
